// Formatting service config (Post a Project, Atrium Stage 7).
//
// Source of truth = the Rails backend, NOT the design prototype:
//   whitelist  app/controllers/api/v1/project_postings_controller.rb (formatting_posting_params)
//   views      app/views/job_postings/steps/formatting/_one.._two.html.erb
//   validation app/models/job_postings/job_postings/step_validations.rb (formatting__one..two)
//   cost       app/models/job_postings/formatting_posting.rb (#cost / #formatting_type_cost)
//   parity     tasks/post-a-project/parity/writing-variants.md (FORMATTING)
//
// NO AI consent. formatting_type submits the enum KEYS paperback_formatting /
// ebook_formatting (the view parameterizes "Paperback"/"eBook" into these); the
// design's plain "paperback"/"ebook" hard-fail the enum. Subtitle is optional.
// trim_size renders (and is required) only for paperback. word_count steps at
// 1,000. Cost CEILS per 10k bucket (the only enhance-text service that ceils);
// the per-10k figures are illustrative — the server PriceList is authoritative.

import { FORMATTING_TYPES, TRIM_SIZES, RATES } from "../options"

// string[] -> [{ value, label }] for select fields.
const asOptions = (arr) => arr.map((v) => ({ value: v, label: v }))

const money = (n) => Math.round(n * 100) / 100

const PAPERBACK = "paperback_formatting"

// Design display labels + option descriptions (service-config.js). The submitted
// VALUE stays the backend enum key (paperback_formatting / ebook_formatting) — the
// design's plain "paperback"/"ebook" hard-fail the enum — so this pairs the design
// label/copy with the backend value (registered as a justified options exception).
// Em dash in the paperback description converted per the no-em-dash rule.
const FORMATTING_LABELS = {
  paperback_formatting: "Paperback Formatting",
  ebook_formatting: "eBook Formatting",
}
const FORMATTING_DESCRIPTIONS = {
  paperback_formatting:
    "Print-ready interior, with margins, running heads, chapter openers and a chosen trim size.",
  ebook_formatting: "Reflowable eBook interior, formatted for Kindle and other readers.",
}

// FORMATTING_TYPES radio options with the design price text "$25 / 10,000 words".
// Server-traced via ctx.rates (ruling C2 / tuw-app:P-001), RATES seed as fallback.
const formattingOptions = (ctx = {}) =>
  FORMATTING_TYPES.map((t) => {
    // Per-key: a null server leaf for this type falls back to the RATES seed for
    // THAT type, never null → NaN.toFixed crash (tuw-app:P-001 / TR-001).
    const rate = ctx.rates?.formatting_types_cost?.[t.value] ?? RATES.FORMATTING_TYPES_COST[t.value]
    return {
      value: t.value,
      label: FORMATTING_LABELS[t.value],
      price: `$${rate} / 10,000 words`,
      sub: FORMATTING_DESCRIPTIONS[t.value],
    }
  })

export const formatting = {
  key: "formatting",
  postingKey: "formatting_posting",
  name: "Formatting",

  steps: () => [
    // Step 1 — Formatting type (formatting__one).
    {
      id: "type",
      title: "Formatting type",
      sub: "Choose the format you need typeset.",
      fields: [
        {
          type: "radio",
          key: "formatting_type",
          required: true,
          options: (v = {}, ctx = {}) => formattingOptions(ctx),
        },
      ],
    },

    // Step 2 — Book details (formatting__two).
    {
      id: "details",
      title: "Book details",
      sub: "A few details to set up the interior.",
      fields: [
        {
          type: "wordcount",
          key: "word_count",
          label: "Word count",
          help: "Minimum 1,000, in increments of 1,000.",
          required: true,
          min: 1000,
          step: 1000,
        },
        { type: "text", key: "title", label: "Title", required: true, placeholder: "Working title" },
        { type: "text", key: "subtitle", label: "Subtitle", required: false },
        {
          type: "select",
          key: "trim_size",
          label: "Trim size",
          required: true,
          when: (values) => values.formatting_type === PAPERBACK,
          blankLabel: "Select Trim Size",
          options: asOptions(TRIM_SIZES),
        },
        {
          type: "file",
          key: "file",
          label: "Requirements file",
          help: "Supported file types: .doc, .docx",
          required: false,
          accept: ".doc,.docx",
          placeholder: "Upload a file",
        },
      ],
    },
  ],

  // Instant estimate for the PriceRail. Ruby (formatting_posting.rb#cost):
  //   ceil(word_count / 10_000) * formatting_type_cost
  // (Ruby steps to the next 10k bucket >= word_count, then divides by 10k.)
  // formatting_type_cost = FORMATTING_TYPES_COST[formatting_type] per 10k words.
  estimate: (values = {}, ctx = {}) => {
    const wc = Number(values.word_count) || 0
    const rate = values.formatting_type
      ? (ctx.rates?.formatting_types_cost?.[values.formatting_type] ?? RATES.FORMATTING_TYPES_COST[values.formatting_type])
      : null
    const lines = []

    if (wc > 0 && rate != null) {
      const buckets = Math.ceil(wc / 10000)
      const label = values.formatting_type === PAPERBACK ? "Paperback" : "eBook"
      lines.push({
        label: `${label} formatting (${wc.toLocaleString()} words)`,
        amount: money(buckets * rate),
      })
    }

    const total = money(lines.reduce((sum, l) => sum + l.amount, 0))

    return { lines, total, isRange: false, framing: "free-to-post" }
  },

  // /post/price entry (camelCase; underscored server-side). cost_fields for
  // formatting_posting = [formatting_type, word_count]. Null until both are set.
  pricePayload: (values = {}) => {
    const wc = Number(values.word_count) || 0
    if (wc <= 0 || !values.formatting_type) return null

    return {
      postingType: "formatting_posting",
      formattingType: values.formatting_type,
      wordCount: wc,
    }
  },
}
